import { useState } from "react";
import { toast } from "react-toastify";
import PageHeader from "../../Component/PageHeader";
import Pill from "../../Component/Pill";
import { FAILED_WEBHOOKS, QUEUES, SERVICES } from "../../utils/staticData";

// STATIC DESIGN — replace SERVICES / FAILED_WEBHOOKS / QUEUES with the monitoring API
const STATUS_ICON = { ok: "ri-checkbox-circle-line", warn: "ri-alert-line", bad: "ri-close-circle-line" };
const STATUS_LABEL = { ok: "up", warn: "slow", bad: "down" };

const SystemHealth = () => {
  const [webhooks, setWebhooks] = useState(FAILED_WEBHOOKS);
  const [bannerOn, setBannerOn] = useState(false);
  const [bannerText, setBannerText] = useState("");
  const degraded = SERVICES.filter(s => s[3] !== "ok").length;

  const replay = (idx: number | "all") => {
    if (idx === "all") {
      toast.success(`${webhooks.length} events replayed successfully`);
      setWebhooks([]);
    } else {
      toast.success("Replayed");
      setWebhooks(w => w.filter((_, i) => i !== idx));
    }
  };

  const toggleBanner = (on: boolean) => {
    if (on && !bannerText.trim()) {
      toast.warning("Write the banner message first");
      return;
    }
    setBannerOn(on);
    toast.success(on ? "Maintenance banner is live for all clients" : "Maintenance banner removed");
  };

  return (
    <div className="container-fluid">
      <div className="cx">
        <PageHeader
          crumb="System health"
          title="System health"
          sub="Backend services, Meta API and webhooks — last 60 minutes."
          actions={<>
            {degraded > 0 && <span className="cx-pill warn" style={{ height: 38, padding: "0 12px" }}><i></i>{degraded} degraded</span>}
            <button type="button" className="cx-btn cx-btn-light" onClick={() => toast.success("Status page updated")}><i className="ri-megaphone-line"></i>Post status update</button>
          </>}
        />

        <div className="cx-grid g21">
          <div className="cx-card">
            <div className="cx-card-h"><h6>Services</h6><span className="sub mono">auto-refresh 30s</span></div>
            <div className="cx-card-b">
              {SERVICES.map(([name, desc, latency, status, incidents]) => (
                <div className="cx-svc" key={name}>
                  <div className={`cx-ic ${status}`}><i className={STATUS_ICON[status]}></i></div>
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">{name}</div>
                    <div className="ds">{desc}</div>
                  </div>
                  {/* one bar per minute, last 60 minutes */}
                  <div className="strip" title="Last 60 minutes">
                    {Array.from({ length: 60 }, (_, i) => (
                      <i key={i} className={incidents.includes(i) ? (status === "bad" ? "b" : "w") : ""}></i>
                    ))}
                  </div>
                  <div className="lat">{latency}</div>
                  <Pill tone={status}>{STATUS_LABEL[status]}</Pill>
                </div>
              ))}
            </div>
          </div>

          <div className="cx-vstack">
            <div className="cx-card">
              <div className="cx-card-h">
                <h6>Failed webhooks</h6>
                <button type="button" className="cx-btn cx-btn-primary cx-btn-xs" disabled={!webhooks.length} onClick={() => replay("all")}>Replay all</button>
              </div>
              <div className="cx-card-b">
                {webhooks.length ? webhooks.map(([source, event, client, code], i) => (
                  <div className="cx-orow" key={event + client}>
                    <div className="cx-ic bad"><i className="ri-webhook-line"></i></div>
                    <div>
                      <div className="t mono" style={{ fontSize: 12 }}>{event}</div>
                      <div className="d">{source} · {client} · <span className="mono">{code}</span></div>
                    </div>
                    <div className="right">
                      <button type="button" className="cx-btn cx-btn-light cx-btn-xs" onClick={() => replay(i)}>Replay</button>
                    </div>
                  </div>
                )) : <div className="cx-empty" style={{ padding: 16 }}><i className="ri-checkbox-circle-line"></i>No failed webhooks</div>}
              </div>
            </div>

            <div className="cx-card">
              <div className="cx-card-h"><h6>Queues</h6></div>
              <div className="cx-card-b">
                {QUEUES.map(([name, waiting, rate]) => (
                  <div key={name} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0" }}>
                    <span>{name}</span>
                    <span className="mono">{waiting} waiting · <span className="muted">{rate}</span></span>
                  </div>
                ))}
              </div>
            </div>

            <div className="cx-card">
              <div className="cx-card-h">
                <h6>Maintenance banner</h6>
                <label className="cx-switch">
                  <input type="checkbox" checked={bannerOn} onChange={(e) => toggleBanner(e.target.checked)} />
                  <span></span>
                </label>
              </div>
              <div className="cx-card-b">
                <textarea className="cx-input" rows={2} placeholder="Shown in the header for every client…" value={bannerText} onChange={(e) => setBannerText(e.target.value)}></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealth;
